/**
 * @deprecated This file is deprecated in favor of credit.service.js
 * 
 * MIGRATION NOTICE:
 * This is the legacy credit service with simpler API interface.
 * Please use credit.service.js instead which provides:
 * - More comprehensive credit management features
 * - Better error handling
 * - Enhanced security and validation
 * - Integration with ai.service.js
 * 
 * Current Usage (to be migrated):
 * - DatabasePage.jsx (line 10) - imports checkCreditAvailability, deductCredits
 * - ApplicationsPage.jsx (line 17) - imports checkCreditAvailability, deductCredits
 * 
 * This file is kept temporarily for backward compatibility.
 * TODO: Update DatabasePage and ApplicationsPage to use credit.service.js
 */

/* eslint-disable no-console */
import api from '../api/api';
import { API_URL } from '../config/constants';

// Show deprecation warning in development
if (process.env.NODE_ENV === 'development') {
  console.warn(
    '%c[DEPRECATED] creditService.js',
    'color: orange; font-weight: bold',
    '\nThis service is deprecated. Please use credit.service.js instead.'
  );
}

// Configuration constants
const API_BASE_URL = API_URL.endsWith('/api') ? API_URL.slice(0, -4) : API_URL;

/**
 * Get the current AI credit balance for a user
 * @param {string} userId - User ID (can be employer or recruiter)
 * @param {object} user - Optional user object with employerId for recruiters
 * @returns {Promise} - Promise that resolves with the user's credit balance
 */
export const getUserCreditBalance = async (userId, user = null) => {
  try {
    // If user is a recruiter, use their employer's ID for credit lookup
    let creditUserId = userId;
    if (user && user.userType === 'recruiter' && user.employerId) {
      creditUserId = user.employerId;
      console.log(`[Credits] Recruiter detected - using employer ID: ${creditUserId}`);
    }

    // Always use real API data - the backend has the actual credits
    const response = await api.get(`/credits/${creditUserId}`);

    // Extract data from PHP wrapper structure
    const responseData = response.data?.data || response.data;

    return {
      success: true,
      data: {
        balance: responseData.balance,
        planLimit: responseData.settings?.monthlyLimit || responseData.planLimit,
        refreshDate: responseData.settings?.refreshDate || responseData.refreshDate,
        usageHistory: responseData.usageHistory || []}
    };
  } catch (error) {
    console.error('Error getting user credit balance:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Failed to get credit balance',
      errorCode: error.response?.data?.errorCode || 'API_ERROR'};
  }
};

/**
 * Check if a user has enough credits for an operation
 * @param {string} userId - User ID
 * @param {number} requiredCredits - Number of credits required
 * @param {object} user - Optional user object for recruiter support
 * @returns {Promise} - Promise that resolves with a boolean indicating if the user has enough credits
 */
export const checkCreditAvailability = async (userId, requiredCredits, user = null) => {
  try {
    const balanceResponse = await getUserCreditBalance(userId, user);
    
    if (!balanceResponse.success) {
      return {
        success: false,
        error: balanceResponse.error,
        errorCode: balanceResponse.errorCode};
    }
    
    const hasEnoughCredits = balanceResponse.data.balance >= requiredCredits;
    
    return {
      success: true,
      data: {
        hasEnoughCredits,
        currentBalance: balanceResponse.data.balance,
        requiredCredits,
        deficit: hasEnoughCredits ? 0 : requiredCredits - balanceResponse.data.balance}
    };
  } catch (error) {
    console.error('Error checking credit availability:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Failed to check credit availability',
      errorCode: error.response?.data?.errorCode || 'API_ERROR'};
  }
};

/**
 * Deduct credits from a user's balance
 * @param {string} userId - User ID
 * @param {number} credits - Number of credits to deduct
 * @param {string} operationType - Type of operation (search, screening, etc.)
 * @param {string} referenceId - Reference ID for the operation (job ID, search ID, etc.)
 * @returns {Promise} - Promise that resolves with the updated credit balance
 */
export const deductCredits = async (userId, credits, operationType, referenceId) => {
  try {
    // Use authenticated API instance
    const response = await api.post('/credits/deduct', {
      userId,
      credits,
      operationType,
      referenceId,
      timestamp: new Date().toISOString()});
    
    // Extract data from PHP wrapper structure
    const responseData = response.data?.data || response.data;
    
    return {
      success: true,
      data: {
        previousBalance: responseData.previousBalance,
        currentBalance: responseData.currentBalance,
        deducted: responseData.deducted || credits,
        transactionId: responseData.transactionId}
    };
  } catch (error) {
    console.error('Error deducting credits:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Failed to deduct credits',
      errorCode: error.response?.data?.errorCode || 'API_ERROR'};
  }
};

/**
 * Add credits to a user's balance (for top-ups or plan refreshes)
 * @param {string} userId - User ID
 * @param {number} credits - Number of credits to add
 * @param {string} source - Source of the credits (purchase, plan_refresh, bonus, etc.)
 * @param {string} referenceId - Reference ID for the transaction
 * @returns {Promise} - Promise that resolves with the updated credit balance
 */
export const addCredits = async (userId, credits, source, referenceId) => {
  try {
    // Use authenticated API instance
    const response = await api.post('/credits/add', {
      userId,
      credits,
      source,
      referenceId,
      timestamp: new Date().toISOString()});
    
    // Extract data from PHP wrapper structure
    const responseData = response.data?.data || response.data;
    
    return {
      success: true,
      data: {
        previousBalance: responseData.previousBalance,
        currentBalance: responseData.currentBalance,
        added: responseData.added || credits,
        transactionId: responseData.transactionId}
    };
  } catch (error) {
    console.error('Error adding credits:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Failed to add credits',
      errorCode: error.response?.data?.errorCode || 'API_ERROR'};
  }
};

/**
 * Get credit usage history for a user
 * @param {string} userId - User ID
 * @param {Object} filters - Filters for the history (date range, operation type, etc.)
 * @returns {Promise} - Promise that resolves with the user's credit usage history
 */
export const getCreditUsageHistory = async (userId, filters = {}) => {
  try {
    // Use authenticated API instance
    const response = await api.get(`/credits/${userId}/history`, {
      params: filters});
    
    // Extract data from PHP wrapper structure
    const responseData = response.data?.data || response.data;
    
    return {
      success: true,
      data: {
        history: responseData.history || responseData.usage || [],
        totalUsed: responseData.totalUsed || responseData.summary?.totalCredits || 0,
        totalAdded: responseData.totalAdded || 0}
    };
  } catch (error) {
    console.error('Error getting credit usage history:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Failed to get credit usage history',
      errorCode: error.response?.data?.errorCode || 'API_ERROR'};
  }
};

/**
 * Get the credit usage for a specific operation
 * @param {string} operationId - Operation ID
 * @returns {Promise} - Promise that resolves with the operation's credit usage
 */
export const getOperationCreditUsage = async (operationId) => {
  try {
    // Use authenticated API instance
    const response = await api.get(`/credits/operation/${operationId}`);
    
    // Extract data from PHP wrapper structure
    const responseData = response.data?.data || response.data;
    
    return {
      success: true,
      data: {
        operationId,
        operationType: responseData.operationType,
        creditsUsed: responseData.creditsUsed,
        timestamp: responseData.timestamp,
        details: responseData.details}
    };
  } catch (error) {
    console.error('Error getting operation credit usage:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Failed to get operation credit usage',
      errorCode: error.response?.data?.errorCode || 'API_ERROR'};
  }
};

/**
 * Calculate the estimated credit cost for an operation
 * @param {string} operationType - Type of operation (search, screening, etc.)
 * @param {Object} parameters - Parameters for the operation
 * @returns {Object} - Estimated credit cost
 */
export const calculateEstimatedCreditCost = (operationType, parameters) => {
  switch (operationType) {
    case 'search':
      // For search, each profile processed costs 1 credit
      return {
        estimatedCredits: parameters.maxProfiles || 50, // Default to 50 if not specified
        costPerItem: 1,
        totalItems: parameters.maxProfiles || 50};
    
    case 'screening':
      // For screening, each applicant screened costs 1 credit
      return {
        estimatedCredits: parameters.applicants?.length || 0,
        costPerItem: 1,
        totalItems: parameters.applicants?.length || 0};
    
    default:
      return {
        estimatedCredits: 0,
        costPerItem: 0,
        totalItems: 0,
        error: `Unknown operation type: ${operationType}`};
  }
};

export default {
  getUserCreditBalance,
  checkCreditAvailability,
  deductCredits,
  addCredits,
  getCreditUsageHistory,
  getOperationCreditUsage,
  calculateEstimatedCreditCost};
